/**
 * NativeSelect component - a native HTML select with hidden options
 * for testing compatibility.
 *
 * TASK-402: styled to visually match the Radix-based SelectTrigger
 * (components/ui/select.tsx) used elsewhere in the same dialogs —
 * `appearance-none` suppresses the browser's native dropdown arrow, and a
 * custom ChevronDown icon (lucide-react) is rendered as an absolutely
 * positioned SIBLING of the <select> (a native <select> can only contain
 * <option>/<optgroup>, so the icon can't live inside it like SelectTrigger's
 * flex layout does). `pr-8` reserves room so the icon never overlaps text.
 *
 * KAN-165: the <select> itself must NOT be `display:flex`. `flex
 * items-center justify-between` on the select was a leftover attempt to
 * mimic SelectTrigger's layout, but the chevron lives OUTSIDE the <select>
 * (see above) so there is nothing for `justify-between` to space — the
 * <select> has only one rendered "child" (its value text). Turning a native
 * <select> into a flex container instead breaks the browser's own vertical
 * centering of that value text once the box is shorter than its default
 * (h-8/h-9 overrides for compact rows): first seen as text sinking to the
 * bottom of the box with `py-2` still present, then — after removing
 * `py-2` but keeping `flex items-center` — as descenders (е.g. the tail of
 * Cyrillic "у") getting clipped even though there was ample box height.
 * Both symptoms went away by dropping `flex`/`items-center`/`justify-between`
 * entirely and letting the <select> render as a normal (non-flex) box —
 * this is the standard, cross-browser-correct way a <select> centers its
 * value vertically, and it accounts for ascenders/descenders correctly
 * because that centering happens via the browser's own font-metric-aware
 * layout, not an author-supplied flex box.
 */
import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface NativeSelectProps
  extends React.SelectHTMLAttributes<HTMLSelectElement> {
  options: Array<{
    value: string;
    label: string;
  }>;
  placeholder?: string;
}

const NativeSelect = React.forwardRef<HTMLSelectElement, NativeSelectProps>(
  ({ className, options, placeholder, ...props }, ref) => {
    return (
      <div className="relative">
        <select
          ref={ref}
          className={cn(
            'h-10 w-full appearance-none rounded-md border border-gray-200 bg-white pl-3 pr-8 text-sm ring-offset-white focus:outline-none focus:ring-2 focus:ring-gray-200 focus:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50',
            className
          )}
          {...props}
        >
          {placeholder && (
            <option value="" disabled={props.value !== ''}>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <ChevronDown
          data-testid="native-select-chevron"
          className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 opacity-50"
        />
      </div>
    );
  }
);
NativeSelect.displayName = 'NativeSelect';

export { NativeSelect };
